import { useForm } from "@mantine/form"
import { Check, CheckCircle, XCircle } from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Group,
  SpinnerOverlay,
  Toast,
} from "@vesatogo/grass-core"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import { BuyerCreditDetails } from "~/components/CreditManagement/BuyerCreditDetails"
import { BuyerDetails } from "~/components/CreditManagement/BuyerDetails"
import { RejectModal } from "~/components/RejectModal"
import { Permission } from "~/constants/Permissions"
import { AppRoutes } from "~/constants/routes"
import {
  useApproveCreditRequestMutation,
  useCreateCreditRequestMutation,
  useCreditWalletDetailQuery,
} from "~/generated/graphql"
import { MONTHS } from "~/utils/customMonths"

const INITIAL_VALUES = {
  invoice: {},
  assessment: {},
}

export const CreditDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isSubmitted, setSubmitted] = useState(false)
  const [showRemarkModal, setRemarkModal] = useState(false)

  const form = useForm<any>({
    initialValues: INITIAL_VALUES,
  })
  const { getInputProps } = form

  const [{ data, fetching: dataFetching }, refetch] =
    useCreditWalletDetailQuery({
      variables: {
        id: id,
      },
      requestPolicy: "network-only",
    })

  const [{ fetching }, addUpdateCredit] = useCreateCreditRequestMutation()
  const [{ fetching: approveFetching }, approveCredit] =
    useApproveCreditRequestMutation()

  const canApprove = useAuthorization(Permission.canApproveCredit)
  const canEdit = useAuthorization(Permission.canEditCreditDetails)
  const disabled =
    form.values?.is_verified === true ? true : id !== "new" ? !canEdit : false

  useEffect(() => {
    const isVerified = data?.credit_wallet_request_by_pk?.is_verified
    if (id !== "new") {
      form.setValues({
        ...data?.credit_wallet_request_by_pk,
        credit_limit_factor:
          data?.credit_wallet_request_by_pk?.requested_credit_limit_factor,
        validity: isVerified
          ? undefined
          : data?.credit_wallet_request_by_pk?.requested_validity,
        payment_cycle_days: isVerified
          ? undefined
          : data?.credit_wallet_request_by_pk?.requested_payment_cycle_days,
        total_amount: isVerified
          ? undefined
          : data?.credit_wallet_request_by_pk?.requested_total_amount,
        user: {
          ...data?.credit_wallet_request_by_pk?.credit_wallet?.user?.user?.[0],
          shared_id: data?.credit_wallet_request_by_pk?.credit_wallet?.user?.id,
          codename:
            data?.credit_wallet_request_by_pk?.credit_wallet?.user?.codename,
        },
        financial_year_assessments:
          data?.credit_wallet_request_by_pk?.financial_year_assessment_cw?.map(
            fin => {
              return {
                ...fin,
                attachments: fin?.attachment ? [fin?.attachment] : undefined,
              }
            }
          ),
        projection_assessments:
          data?.credit_wallet_request_by_pk?.projection_assessment_cw?.map(
            pro => {
              const month = Object.values(MONTHS)?.find(
                mo => mo?.days === pro?.tenure
              )
              return { ...pro, tenure: month }
            }
          ),
        trade_assessments:
          data?.credit_wallet_request_by_pk?.trade_assessment_cw?.map(pro => {
            const month = Object.values(MONTHS)?.find(
              mo => mo?.days === pro?.tenure
            )
            return { ...pro, tenure: month }
          }),
      })
    }
  }, [data])

  const onSubmit = async () => {
    const state = form.values
    const tradeAssessment = state?.trade_assessments?.[0]
    const projectionAssessment = state?.projection_assessments?.[0]
    const financialYearAssessment = state?.financial_year_assessments
    const isNew = state?.isNew
    setSubmitted(true)

    if (!isNew && (!tradeAssessment || !projectionAssessment)) {
      if (!tradeAssessment) {
        return toast.custom(
          <Toast intent="danger" title={"Please enter SetuTrade Assessment"} />
        )
      }
      if (!projectionAssessment) {
        return toast.custom(
          <Toast intent="danger" title={"Please enter Projection Assessment"} />
        )
      }

      return toast.custom(
        <Toast intent="danger" title={"Please enter all mandatory fields"} />
      )
    }

    if (isNew && !projectionAssessment) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Please enter Projection Assessment to proceed!"}
        />
      )
    }

    if (!state?.credit_limit_factor) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Please enter Credit Limit Factor to proceed!"}
        />
      )
    }
    if (!state?.requested_total_amount) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Please enter Credit Limit to proceed!"}
        />
      )
    }
    if (!state?.requested_validity) {
      return toast.custom(
        <Toast intent="danger" title={"Please enter Validity to proceed!"} />
      )
    }
    if (!state?.requested_payment_cycle_days) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Please enter Payment Cycle Days to proceed!"}
        />
      )
    }

    const { error } = await addUpdateCredit({
      input: {
        id: id !== "new" ? id : undefined,
        requested_credit_limit_factor: Number(state?.credit_limit_factor),
        requested_total_amount: Number(state?.requested_total_amount),
        requested_validity: Number(state?.requested_validity),
        requested_payment_cycle_days: Number(
          state?.requested_payment_cycle_days
        ),
        trade_assessment: tradeAssessment
          ? {
              end_date: dayjs(tradeAssessment?.end_date).format("YYYY-MM-DD"),
              payment_cycle: Number(tradeAssessment?.payment_cycle),
              start_date: dayjs(tradeAssessment?.start_date).format(
                "YYYY-MM-DD"
              ),
              tenure: tradeAssessment?.tenure?.days,
            }
          : undefined,
        projection_assessment: projectionAssessment
          ? {
              end_date: dayjs(projectionAssessment?.end_date).format(
                "YYYY-MM-DD"
              ),
              payment_cycle: Number(projectionAssessment?.payment_cycle),
              start_date: dayjs(projectionAssessment?.start_date).format(
                "YYYY-MM-DD"
              ),
              tenure: projectionAssessment?.tenure?.days,
              total_sales: Number(projectionAssessment?.total_sales),
            }
          : undefined,
        financial_year_assessments: financialYearAssessment?.map(fin => {
          return {
            id:
              state?.is_verified === true || state?.is_verified === false
                ? undefined
                : Number(fin?.id)
                ? fin?.id
                : undefined,
            attachment_id: fin?.attachments?.[0]?.id,
            year_id: fin?.financial_year?.id,
            capital: Number(fin?.capital),
            current_liabilities: Number(fin?.current_liabilities),
            current_assets: Number(fin?.current_assets),
            fixed_assets: Number(fin?.fixed_assets),
            profit_loss: Number(fin?.profit_loss),
            total_sales: Number(fin?.total_sales),
          }
        }),
        user_id: state?.user?.shared_id,
        remark: state?.remark,
      },
    })
    if (error) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Something went wrong! " + error.message}
        />
      )
    }
    // if (id !== "new") {

    // }
    toast.custom(<Toast title={"Credit Saved Successfully!"} />)
    navigate(AppRoutes.credit)
  }

  const onApprove = async (status = true, remark = "") => {
    const state = form.values
    const { error } = await approveCredit({
      input: {
        credit_wallet_request_id: id as string,
        total_amount: state?.requested_total_amount
          ? Number(state?.requested_total_amount)
          : undefined,
        validity: Number(state?.requested_validity),
        payment_cycle_days: Number(state?.requested_payment_cycle_days),
        is_approved: status,
        remark: remark,
      },
    })

    if (error) {
      return toast.custom(
        <Toast
          intent="danger"
          title={"Something went wrong! " + error.message}
        />
      )
    }
    refetch({
      requestPolicy: "network-only",
    })
    return toast.custom(
      <Toast
        title={`Successfully ${status ? "approved" : "rejected"} Credit Limit!`}
      />
    )
  }

  return (
    <>
      <header className="bg-gray-100 shadow-sm p-3 border-b-gray-300 border-b flex justify-between">
        <SpinnerOverlay show={dataFetching} />
        <Breadcrumbs
          items={[
            {
              text: "Credit",
              link: AppRoutes.credit,
              isActive: false,
            },
            {
              text: id?.toString() || "New",
              link: AppRoutes.creditDetail(id?.toString() || "new"),
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
      </header>
      <Group className="flex gap-4 px-5 pt-8 pb-16 relative">
        <div className="w-[80%] mx-auto">
          <BuyerDetails
            form={form}
            inputProps={getInputProps}
            isDisabled={id === "new" ? false : true}
          />
          {form?.values?.user && (
            <BuyerCreditDetails
              form={form}
              inputProps={getInputProps}
              isDisabled={disabled}
              isSubmitted={isSubmitted}
              defaultSegment={id === "new" ? "credit_assessment" : "invoice"}
            />
          )}
        </div>
      </Group>
      <footer
        className={classNames(
          "fixed bottom-0 bg-white shadow-overlay p-3 w-[calc(100%-64px)] rounded-t-2xl text-right border-b-2 border-green-400"
        )}
      >
        <div
          className={classNames(
            id === "new"
              ? "flex justify-end"
              : canApprove
              ? "flex justify-between"
              : ""
          )}
        >
          <Button
            text="Reject"
            intent="danger"
            leftIcon={<XCircle />}
            variant="outline"
            hidden={!canApprove || id === "new"}
            disabled={
              form.values.is_verified === true ||
              form.values.is_verified === false
            }
            onClick={() => {
              setRemarkModal(true)
            }}
            loading={approveFetching}
          />
          <div className={classNames(canApprove ? "flex gap-3" : "")}>
            <Button
              text="Approve"
              leftIcon={<CheckCircle />}
              variant="outline"
              onClick={() => onApprove(true)}
              loading={approveFetching}
              hidden={!canApprove || id === "new"}
              disabled={
                form.values.is_verified === true ||
                form.values.is_verified === false
              }
            />
            <Button
              text={"Send for Approval"}
              className="mr-10"
              onClick={onSubmit}
              loading={fetching}
              leftIcon={<Check />}
              hidden={disabled}
              disabled={form.values?.status?.id === "Requested"}
            />
          </div>
        </div>
      </footer>
      <RejectModal
        isOpen={showRemarkModal}
        setOpen={setRemarkModal}
        onComplete={e => {
          if (!e) {
            return
          }
          onApprove(false, e)
        }}
      />
    </>
  )
}

export default CreditDetails
